import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ProductImage } from "@/components/ProductImage";
import { PriceBlock } from "@/components/PriceBlock";
import { MarginBadge } from "@/components/MarginBadge";
import { StockBadge } from "@/components/StockBadge";
import { PackagingHint } from "@/components/PackagingHint";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { expectedDeliveryDate, formatDeliveryDate, getLocalizedField, normalizeQuantity, productImageUrl, stockLevel, isOrderable, unitLabel } from "@/lib/product";
import { productBrand } from "@/lib/brands";

export const ProductCard = ({ product }) => {
  const { t, i18n } = useTranslation();
  const { addLine } = useCart();
  const { account } = useAuth();
  const naam = getLocalizedField(product, "naam", i18n.language);
  const merk = productBrand(product);
  const outOfStock = stockLevel(product) === "out";
  const supplier = stockLevel(product) === "supplier";
  const orderable = isOrderable(product, account);

  return (
    <Card
      className="card-lift group flex h-full flex-col overflow-hidden rounded-2xl border bg-card"
      data-testid="product-card"
      data-product-id={product.id}
    >
      <Link
        to={`/producten/${product.id}`}
        className="relative block"
        data-testid="product-card-open-link"
        aria-label={naam}
      >
        <MarginBadge product={product} />
        <ProductImage
          src={productImageUrl(product.hoofdfoto)}
          alt={t("product.imageAlt", { naam })}
          className="aspect-[4/5] w-full bg-sand-light"
          imgClassName="p-4 transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            {merk && (
              <span
                className="truncate font-display text-[11px] font-bold uppercase tracking-widest text-primary"
                data-testid="product-card-brand"
              >
                {merk.naam}
              </span>
            )}
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {product.sku}
            </span>
          </span>
          <StockBadge product={product} />
        </div>

        <Link to={`/producten/${product.id}`} className="mt-2">
          <h3
            className="line-clamp-2 font-display text-base font-semibold leading-snug tracking-tight transition-colors hover:text-primary"
            data-testid="product-card-title"
          >
            {naam}
          </h3>
        </Link>

        <div className="mt-3">
          <PriceBlock
            product={product}
            perUnit={product.eenheid ? t("price.perUnit", { eenheid: unitLabel(product.eenheid, 1, i18n.language) }) : ""}
          />
          {!orderable && (
            <p
              className="mt-1 text-xs text-muted-foreground"
              data-testid="product-card-unavailable-note"
            >
              {t("stock.unavailableNote")}
            </p>
          )}
          {supplier && (
            <p className="mt-1 text-xs font-medium text-[hsl(var(--warning))]" data-testid="product-card-supplier-note">
              {t("stock.supplierShort", { datum: formatDeliveryDate(expectedDeliveryDate(), i18n.language) })}
            </p>
          )}
          {orderable && outOfStock && (
            <p
              className="mt-1 text-xs text-[hsl(var(--danger))]"
              data-testid="product-card-backorder-note"
            >
              {t("stock.backorder")}
            </p>
          )}
          <PackagingHint product={product} testId="product-card-packaging-hint" />
        </div>

        <div className="mt-4 flex items-center gap-2 pt-1">
          <Button
            className="flex-1 whitespace-normal"
            disabled={!orderable}
            onClick={() => {
              const qty = normalizeQuantity(product, 1);
              addLine(product, qty);
              toast.success(t("product.added", { naam }));
            }}
            data-testid="product-card-add-to-cart-button"
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            {orderable ? t("catalog.addToCart") : t("stock.unavailable")}
          </Button>
          <Button variant="outline" asChild data-testid="product-card-view-button">
            <Link to={`/producten/${product.id}`}>{t("catalog.view")}</Link>
          </Button>
        </div>
      </div>
    </Card>
  );
};
